import {Component, Input, OnInit} from '@angular/core';

import {CommonService} from '../../services/common.service';

export enum FusionChartType {
  Column2d = 'Column2d',
  line = 'line',
  msline = 'msline'
}

export enum FusionChartAnnotationType {
  Text = 'text',
  Image = 'image',
  Rectangle = 'rectangle',
  Polygon = 'polygon',
  Line = 'line',
  Circle = 'circle',
  Arc = 'arc',
  Path = 'path',
}

@Component({
  selector: 'vrm-fusion-chart',

  template: `
    <div class="fusion-charts-container">
      <fusioncharts
        [width]="width"
        [height]="height"
        [type]="chartType"
        [dataSource]="dataSource">
      </fusioncharts>
    </div>
  `,
  styles: [
    `
      .fusion-charts-container {
        height: 40vh;
        width: 100%;
        position: relative;
      }

      fusioncharts {
        position: absolute;
        margin-top: 10px;
        margin-bottom: 20px;
        height: 100%;
        width: 100%;
      }
    `,
  ],
})
export class VrmFusionChartComponent implements OnInit {
  dataSource: string | {};

  @Input() chartId: string | number;
  @Input() width: string = '100%';
  @Input() height: string = '100%';
  @Input() chartType: FusionChartType = FusionChartType.line;
  @Input() chartData: any;

  constructor(private cService: CommonService) {
  }

  ngOnInit() {
    this.chartType = this.chartData.type;
    this.dataSource = this.buildDataSource(this.chartType, this.chartData);
  }

  private buildDataSource(chartType: FusionChartType, chartData: any) {
    if (this.isMultiDataSetChart(chartType)) {
      return this.buildDataSourceForMultiSeriesChart(chartData);
    }

    return this.buildSingleSeriesDataSource(chartData);
  }

  isMultiDataSetChart(type: string): boolean {
    return [FusionChartType.msline].includes(FusionChartType[type]);
  }

  translateChartText(text: string): string {
    return this.cService.getSingleTranslation(`CHART.${text}`, text);
  }

  private getDataWithTranslatedLabels(data: any[]): any[] {
    return data.map((dataPoint) => {
      return {
        label: this.translateChartText(dataPoint.label),
        value: dataPoint.value
      };
    });
  }

  private getChartConfig(chartData) {
    return {
      ...chartData.options,
      caption: this.translateChartText(chartData.options.caption),
      subCaption: chartData.options.subCaption ? this.translateChartText(chartData.options.subCaption) : null,
      xAxisName: chartData.options.xAxisName ? this.translateChartText(chartData.options.xAxisName) : null,
      yAxisName: chartData.options.yAxisName ? this.translateChartText(chartData.options.yAxisName) : null,
      showValues: chartData.options.showValues ?? true, // Showing values over points by default
      theme: 'fusion',
      captionPadding: chartData.options.captionPadding ?? null
    };
  }

  private buildSingleSeriesDataSource(chartData): any {
    this.chartData = {
      ...this.chartData,
      data: this.getDataWithTranslatedLabels(this.chartData.data)
    };
    return {
      chart: this.getChartConfig(chartData),
      data: chartData.data
    };
  }

  private buildDataSourceForMultiSeriesChart(chartData: any): any {
    return {
      chart: this.getChartConfig(chartData),
      categories: this.getCategoriesTranslated(chartData.categories),
      dataset: this.getDatasetsTranslated(chartData.dataset),
      annotations: chartData.annotations ? this.processAnnotations(chartData.annotations) : null
    };
  }

  private getCategoriesTranslated(categories: any): any {
    return categories.map((categoryItem: any) => {
      return {
        ...categoryItem,
        category: categoryItem.category.map((categoryLabel) => {
          return {label: this.translateChartText(categoryLabel.label)};
        })
      };
    });
  }

  private getDatasetsTranslated(datasets: any[]): any[] {
    return datasets.map((dataset) => {
      return {
        ...dataset,
        seriesname: this.translateChartText(dataset.seriesname)
      };
    });
  }

  private getAnnotationsTranslated(annotations: any) {
    return {
      groups: annotations.groups.map((group: any) => {
        return {
          ...group,
          items: group.items.map((item: any) => {
            if (item.type === FusionChartAnnotationType.Text) {
              return {
                ...item,
                text: this.translateChartText(item.text)
              };
            }

            return item;
          })
        };
      })
    };
  };

  private processAnnotations(annotations: any) {
    let annotationsTranslated = this.getAnnotationsTranslated(annotations);
    let legendGroup = annotationsTranslated.groups.find((group) => group.id === 'legend');

    if (legendGroup) {
      this.positionLegend(legendGroup);
    }

    return annotationsTranslated;
  }

  private positionLegend(legendGroup: any) {
    let legendIcons = legendGroup.items.filter((item: any) => item.type !== FusionChartAnnotationType.Text);
    let legendTexts = legendGroup.items.filter((item: any) => item.type === FusionChartAnnotationType.Text);
    const fontSize = 9;
    const iconSize = 9;
    const numberOfCharacters = legendTexts.map((itemText: any) => itemText.text.length).reduce((a, b) => a + b, 0);
    const legendSize = (numberOfCharacters * fontSize) + (legendTexts.length * iconSize);
    let xOffset = -(legendSize / 2);
    const gapBetweenIconAndText = iconSize + 3
    const gapBetweenItems = this.cService.selectedLanguage.code === 'jp' ? 25 : 15;

    legendIcons.map((itemIcon: any) => {
      let itemText = legendTexts.find((itemText: any) => itemText.id === itemIcon.id);
      itemIcon.x = `$subCaptionStartX + ${xOffset}`;
      itemIcon.y = `$subCaptionEndY + 15`;
      itemText.x = `$subCaptionStartX + ${xOffset + gapBetweenIconAndText}`;
      itemText.y = `$subCaptionEndY + 15`;

      xOffset += itemText.text.length * gapBetweenItems;
    });
  }
}
